// utils/db.js

import { openDB } from 'idb'

const DB_NAME = 'InnovaVantageDB'
const WELLS_LIST_STORE_NAME = 'wellsList'
const MARKERS_STORE_NAME = 'mapMarkers'
const WEATHER_STORE_NAME = 'weatherBackgrounds'
const LAYER_STORE_NAME = 'geoLayers'
const ORG_ICONS_STORE_NAME = 'orgIcons'
const WPC_TEMPLATES_STORE_NAME = 'wallplotComposerTemplates'
const OFFSET_WELLS_STORE_NAME = 'offsetWells'
const PLANS_LIST_STORE_NAME = 'plansList'
const DB_VERSION = 16 // bump this if you add a new store--otherwise the upgrade function won't run
const OFFSET_WELL_EXPIRY_MS = 14 * 24 * 60 * 60 * 1000
const CLEANUP_INTERVAL_MS = 24 * 60 * 60 * 1000

export const initDB = async () => {
  // uncomment this line to simulate an IndexedDB failure for testing purposes
  // throw new Error('Simulated IndexedDB failure')

  const db = await openDB(DB_NAME, DB_VERSION, {
    upgrade(db) {
      if (!db.objectStoreNames.contains(OFFSET_WELLS_STORE_NAME)) {
        const offsetStore = db.createObjectStore(OFFSET_WELLS_STORE_NAME, { keyPath: 'compoundKey' })
        offsetStore.createIndex('organization', 'organization', { unique: false })
        offsetStore.createIndex('wellName', 'wellName', { unique: false })
      }

      if (!db.objectStoreNames.contains(MARKERS_STORE_NAME)) {
        db.createObjectStore(MARKERS_STORE_NAME, { keyPath: 'filename' }) // filename as unique key
      }

      if (!db.objectStoreNames.contains(WEATHER_STORE_NAME)) {
        db.createObjectStore(WEATHER_STORE_NAME, { keyPath: 'filename' }) // filename as unique key
      }

      if (!db.objectStoreNames.contains(LAYER_STORE_NAME)) {
        db.createObjectStore(LAYER_STORE_NAME)
      }

      if (!db.objectStoreNames.contains(ORG_ICONS_STORE_NAME)) {
        db.createObjectStore(ORG_ICONS_STORE_NAME, { keyPath: 'filename' }) // filename as unique key
      }

      if (db.objectStoreNames.contains(WELLS_LIST_STORE_NAME)) {
        db.deleteObjectStore(WELLS_LIST_STORE_NAME)
      }

      if (!db.objectStoreNames.contains(WPC_TEMPLATES_STORE_NAME)) {
        db.createObjectStore(WPC_TEMPLATES_STORE_NAME)
      }

      // compound key: "organization|actualWell"
      const store = db.createObjectStore(WELLS_LIST_STORE_NAME, { keyPath: 'compoundKey' })
      store.createIndex('organization', 'organization', { unique: false })
      store.createIndex('actualWell', 'actualWell', { unique: false })

      // compond key: "organization|planName"
      if (!db.objectStoreNames.contains(PLANS_LIST_STORE_NAME)) {
        const plansListStore = db.createObjectStore(PLANS_LIST_STORE_NAME, { keyPath: 'compoundKey' })
        plansListStore.createIndex('organization', 'organization', { unique: false })
        plansListStore.createIndex('planName', 'planName', { unique: false })
      }
    },
  })

  cleanupOffsetWells(db).catch(console.warn)
  return db
}

const cleanupOffsetWells = async (db) => {
  try {
    const lastCleanup = localStorage.getItem('offsetWellCleanup')

    if (lastCleanup && Date.now() - Number(lastCleanup) < CLEANUP_INTERVAL_MS) {
      return
    }

    const tx = db.transaction(OFFSET_WELLS_STORE_NAME, 'readwrite')
    const store = tx.objectStore(OFFSET_WELLS_STORE_NAME)

    const wells = await store.getAll()
    const cutoff = Date.now() - OFFSET_WELL_EXPIRY_MS

    for (const well of wells) {
      if (!well.lastAccessed || well.lastAccessed < cutoff) {
        await store.delete(well.compoundKey)
      }
    }

    await tx.done

    localStorage.setItem('offsetWellCleanup', Date.now().toString())
  } catch (err) {
    console.warn('Failed to cleanup offset wells:', err)
  }
}

export const saveOffsetWellToDB = async (offsetWells) => {
  if (!offsetWells?.wellName) return

  try {
    const db = await initDB()

    const organization = (offsetWells.organization || 'UNKNOWN').trim().toUpperCase()

    const wellName = offsetWells.wellName.trim().toUpperCase()

    const compoundKey = `${organization}|${wellName}`

    await db.put(OFFSET_WELLS_STORE_NAME, {
      ...offsetWells,
      compoundKey,
      cachedAt: Date.now(),
      lastAccessed: Date.now(),
    })
  } catch (err) {
    console.warn('Failed to save offset well:', err)
  }
}

export const loadOffsetWellsFromDB = async (organization, wellName) => {
  try {
    const db = await initDB()

    const compoundKey = `${organization.trim().toUpperCase()}|${wellName.trim().toUpperCase()}`

    const record = await db.get(OFFSET_WELLS_STORE_NAME, compoundKey)

    if (!record) {
      return []
    }

    record.lastAccessed = Date.now()
    await db.put(OFFSET_WELLS_STORE_NAME, record)

    return record.offsetWells || []
  } catch (err) {
    console.warn('Failed to load offset wells:', err)
    return []
  }
}

// Wells List
export const saveWellsListToDB = async (wells, org, units) => {
  if (!Array.isArray(wells) || wells.length === 0) return

  try {
    const db = await initDB()
    const tx = db.transaction(WELLS_LIST_STORE_NAME, 'readwrite')
    const store = tx.objectStore(WELLS_LIST_STORE_NAME)

    const now = new Date().toISOString()

    wells.forEach((well) => {
      if (!well) return
      const organization = well.organization || org || 'UNKNOWN'
      const actualWell = well.actualWell || 'UNKNOWN'

      const compoundKey = `${organization.trim().toUpperCase()}|${actualWell.trim().toUpperCase()}`
      const record = {
        ...well,
        cachedAt: now,
        selectedOrg: org,
        selectedUnits: units,
        organization,
        compoundKey,
      }

      store.put(record)
    })

    await tx.done
  } catch (err) {
    console.warn('Failed to save wells:', err)
  }
}
export const loadWellsListFromDB = async () => {
  const db = await initDB()
  return db.getAll(WELLS_LIST_STORE_NAME)
}

export const clearWellsList = async () => {
  try {
    const db = await initDB()
    const tx = db.transaction(WELLS_LIST_STORE_NAME, 'readwrite')
    await tx.objectStore(WELLS_LIST_STORE_NAME).clear()
    await tx.done
  } catch (err) {
    console.warn('Failed to clear wells:', err)
  }
}

// Map Markers
export const saveMarkersToDB = async (markers) => {
  const db = await initDB()
  const tx = db.transaction(MARKERS_STORE_NAME, 'readwrite')
  const store = tx.objectStore(MARKERS_STORE_NAME)
  markers.forEach((marker) => {
    store.put(marker) // replaces if filename already exists
  })
  await tx.done
}

export const loadMarkersFromDB = async () => {
  const db = await initDB()
  return db.getAll(MARKERS_STORE_NAME)
}

export const clearMarkers = async () => {
  const db = await initDB()
  const tx = db.transaction(MARKERS_STORE_NAME, 'readwrite')
  await tx.objectStore(MARKERS_STORE_NAME).clear()
  await tx.done
}

// Weather Backgrounds
export const saveWeatherBackgroundsToDB = async (backgrounds) => {
  const db = await initDB()
  const tx = db.transaction(WEATHER_STORE_NAME, 'readwrite')
  const store = tx.objectStore(WEATHER_STORE_NAME)
  backgrounds.forEach((background) => {
    store.put(background) // replaces if filename already exists
  })
  await tx.done
}

export const loadWeatherBackgroundsFromDB = async () => {
  const db = await initDB()
  return db.getAll(WEATHER_STORE_NAME)
}

export const clearWeatherBackgrounds = async () => {
  const db = await initDB()
  const tx = db.transaction(WEATHER_STORE_NAME, 'readwrite')
  await tx.objectStore(WEATHER_STORE_NAME).clear()
  await tx.done
}

// Geo Layers
export const saveLayerToDB = async (key, data) => {
  try {
    const db = await initDB()
    const tx = db.transaction(LAYER_STORE_NAME, 'readwrite')
    await tx.objectStore(LAYER_STORE_NAME).put(data, key)
    await tx.done
  } catch (err) {
    console.warn(`Failed to store layer ${key} in IndexedDB:`, err)
  }
}

export const loadLayerFromDB = async (key) => {
  try {
    const db = await initDB()
    return await db.transaction(LAYER_STORE_NAME).objectStore(LAYER_STORE_NAME).get(key)
  } catch (err) {
    console.warn(`Failed to load layer ${key} from IndexedDB:`, err)
    return null
  }
}

// Org Icons Backgrounds
export const saveOrgIconsToDB = async (icons) => {
  const db = await initDB()
  const tx = db.transaction(ORG_ICONS_STORE_NAME, 'readwrite')
  const store = tx.objectStore(ORG_ICONS_STORE_NAME)
  icons.forEach((icon) => {
    store.put(icon) // replaces if filename already exists
  })
  await tx.done
}

export const loadOrgIconsFromDB = async () => {
  const db = await initDB()
  return db.getAll(ORG_ICONS_STORE_NAME)
}

export const clearOrgIcons = async () => {
  const db = await initDB()
  const tx = db.transaction(ORG_ICONS_STORE_NAME, 'readwrite')
  await tx.objectStore(ORG_ICONS_STORE_NAME).clear()
  await tx.done
}

export const saveWPCTemplateToDB = async (key, data) => {
  try {
    const db = await initDB()
    const tx = db.transaction(WPC_TEMPLATES_STORE_NAME, 'readwrite')
    await tx.objectStore(WPC_TEMPLATES_STORE_NAME).put(data, key)
    await tx.done
  } catch (err) {
    console.warn(`Failed to store template ${key} in IndexedDB:`, err)
  }
}

export const loadWPCTemplateFromDB = async (key) => {
  try {
    const db = await initDB()
    return db.get(WPC_TEMPLATES_STORE_NAME, key)
  } catch (err) {
    console.warn(`Failed to load template ${key} from IndexedDB:`, err)
    return null
  }
}

// Plan List
export const savePlanListToDB = async (wells, org, units) => {
  if (!Array.isArray(wells) || wells.length === 0) return

  try {
    const db = await initDB()
    const tx = db.transaction(PLANS_LIST_STORE_NAME, 'readwrite')
    const store = tx.objectStore(PLANS_LIST_STORE_NAME)

    const now = new Date().toISOString()

    wells.forEach((well) => {
      if (!well) return
      const organization = well.organization || org || 'UNKNOWN'
      const planName = well.planName || 'UNKNOWN'

      const compoundKey = `${organization.trim().toUpperCase()}|${planName.trim().toUpperCase()}`
      const record = {
        ...well,
        cachedAt: now,
        selectedOrg: org,
        selectedUnits: units,
        organization,
        planName,
        compoundKey,
      }

      store.put(record)
    })

    await tx.done
  } catch (err) {
    console.warn('Failed to save plan list:', err)
  }
}

export const loadPlanListFromDB = async () => {
  const db = await initDB()
  return db.getAll(PLANS_LIST_STORE_NAME)
}

export const clearPlanList = async () => {
  try {
    const db = await initDB()
    const tx = db.transaction(PLANS_LIST_STORE_NAME, 'readwrite')
    await tx.objectStore(PLANS_LIST_STORE_NAME).clear()
    await tx.done
  } catch (err) {
    console.warn('Failed to clear plan list:', err)
  }
}
